import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { DischargeLoanForm } from '@/components/DischargeLoanForm';
import { Home, Car, ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg text-muted-foreground mb-6 leading-relaxed text-justify transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

const DischargeLoanPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  return (
    <div className="min-h-screen">
      <Header />
      <SEO
        title="Discharge Mortgages, Car Loans & Credit Cards | Freeman & Crown Law"
        description="Professional legal assistance to discharge your mortgage, car loan, or credit card debt through proper lawful procedures and documentation. Expert guidance for debt elimination."
        image="/lovable-uploads/8c630ffa-2bac-4996-be73-277885cc03f3.png"
        url={window.location.origin + '/services/discharge-loan'}
        type="website"
      />
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-6">
              <div className="flex items-center gap-1">
                <Home className="h-6 w-6 text-gold-light" />
                <Car className="h-6 w-6 text-gold-light" />
              </div>
            </div>
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Discharge Mortgages, Car Loans & Credit Cards
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Lawful discharge of mortgages, car loans, and credit card debt through proper constitutional procedures and documentation
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <AnimatedParagraph delay={200}>
              We provide clients with a powerful pathway to relieve themselves from the weight of mortgages and car loans. Through lawful, structured processes rooted in commercial law, we help discharge debt obligations and, when necessary, escalate to litigation on your behalf.
            </AnimatedParagraph>

            <h2 className="font-rockwell text-2xl font-semibold text-primary mt-12 mb-6">How It Works</h2>
            
            <AnimatedParagraph delay={400}>
              <strong>Step One: Promissory Note Submission</strong><br />
              We begin by preparing and submitting a promissory note to your lender. Under the Uniform Commercial Code (UCC), negotiable instruments such as promissory notes can serve as a means of settling and discharging certain debts. This step asserts your rights while opening the door to resolution.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={600}>
              <strong>Step Two: Response or Dishonor</strong><br />
              If the lender accepts, the obligation is discharged. If they dishonor or reject the instrument, this creates grounds for additional remedies under law.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={800}>
              <strong>Step Three: Escalation and Enforcement</strong><br />
              If dishonor occurs, we proceed with additional measures: administrative processes, targeted filings, and—when necessary—litigation. At every stage, we ensure that your rights are preserved and advanced.
            </AnimatedParagraph>

            <h2 className="font-rockwell text-2xl font-semibold text-primary mt-12 mb-6">Our Donation-Based Structure</h2>
            
            <AnimatedParagraph delay={1000}>
              Freeman & Crown operates as a 508(c)(1)(A) non-taxable entity, which means all contributions are treated as donations.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={1200}>
              <strong>Startup Donation:</strong> To begin, there is a $200 processing donation. This covers initial filings, document preparation, and startup administration.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={1400}>
              <strong>Flat Donation Upon Success:</strong> The $2,000 flat donation is only due once the desired result is achieved—namely, the discharge of your mortgage, car loan, or credit card debt.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={1600}>
              This ensures you can begin the process without taking on a large upfront cost, while aligning our incentives with your success.
            </AnimatedParagraph>

            <h2 className="font-rockwell text-2xl font-semibold text-primary mt-12 mb-6">Why Clients Choose Us</h2>
            
            <AnimatedParagraph delay={1800}>
              <strong>Freedom from heavy debts:</strong> Mortgages and car notes no longer have to feel like lifelong chains.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={2000}>
              <strong>Integrity in process:</strong> Every step is transparent, rooted in law, and geared toward your best interest.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={2200}>
              <strong>Donation model that protects you:</strong> You only provide the larger donation once the goal is reached.
            </AnimatedParagraph>
            
            <AnimatedParagraph delay={2400}>
              <strong>Persistent advocacy:</strong> From the first filing to litigation, if necessary, we stand with you until resolution.
            </AnimatedParagraph>

          {/* Call to Action */}
          <div className="bg-gradient-to-br from-primary/10 to-gold/20 rounded-2xl p-8 md:p-12 text-center border border-gold/30 mt-12">
            <h2 className="font-rockwell text-3xl md:text-4xl font-semibold text-primary mb-6">
              Ready to Discharge Your Loans?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              Start your journey to freedom from mortgage and car loan debt. Our expert team will guide you through every step of the lawful discharge process.
            </p>
            
            <Collapsible open={isFormOpen} onOpenChange={setIsFormOpen}>
              <CollapsibleTrigger asChild>
                <div className="flex justify-center">
                  <Button 
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 text-lg"
                  >
                    Get Started
                    <ChevronDown className={`ml-2 h-5 w-5 transition-transform ${isFormOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-8">
                <DischargeLoanForm onSuccess={() => setIsFormOpen(false)} />
              </CollapsibleContent>
            </Collapsible>
          </div>
          </div>
        </div>
      </section>

      {/* Educate Yourself Card */}
      <section className="pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <AnimatedParagraph delay={2600}>
            <Link to="/category/debt-discharge/videos" className="block">
              <div className="p-8 bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20">
                <div className="mb-4">
                  <div className="w-12 h-1 bg-primary mb-6"></div>
                </div>
                <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                  Wanna do for self for free?
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6 text-sm">
                  We are committed to protecting the constitutional rights of all men and women on the land---and because of this, we make all of our resources and processes completely free so that you are empowered to act on your own without needing anyone or anything or even money for that matter. We give it all for free and if you want to do it that way just click the button below.
                </p>
                <div className="flex justify-start">
                  <Button 
                    variant="link"
                    className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                  >
                    Educate Myself and Act <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Link>
          </AnimatedParagraph>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default DischargeLoanPage;